import { useMemo, useState } from 'react';

import { ApiClient, formatDateTime, formatMoney, useAsync } from '@clientma/core';
import { Alert, Badge, Button, Card, Input, Skeleton, useToast } from '@clientma/ui';

import { providerApi, type ProviderPaymentRow } from '../api';

/**
 * Payments a customer says they have made, waiting on somebody here to agree.
 *
 * This is the settlement step for bank transfer, which is how most customers in India, Singapore
 * and Malaysia actually pay. The money has already moved; what is missing is a person matching a
 * remittance to an invoice. Until that happens the invoice stays open and the customer keeps
 * being chased for something they have already done — which is why this queue is the first thing
 * the overview shouts about.
 */
export default function Payments({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const { data, isLoading, error, reload } = useAsync(
    (signal) => provider.paymentsAwaitingReview(signal),
    [provider],
  );

  if (isLoading) return <Skeleton height={280} />;

  if (error) {
    return (
      <Alert tone="danger" title="The payment queue could not be loaded">
        {error.message}
      </Alert>
    );
  }

  const waiting = data ?? [];

  return (
    <div className="cma-stack">
      {waiting.length === 0 ? (
        <Alert tone="success" title="Nothing waiting">
          Every declared payment has been confirmed or rejected.
        </Alert>
      ) : (
        <>
          <p className="provider-muted">
            {waiting.length} {waiting.length === 1 ? 'payment is' : 'payments are'} waiting. Check
            each against the bank statement before confirming — confirming settles the invoice.
          </p>

          {waiting.map((payment) => (
            <PaymentCard key={payment.id} api={api} payment={payment} onReviewed={reload} />
          ))}
        </>
      )}

      {/* Shown whether or not anything is waiting. An empty queue with nothing beneath it reads
          as a screen that failed to load, and the reviewed history is the evidence that it did. */}
      <Reviewed api={api} />
    </div>
  );
}

/**
 * What has already been decided, most recent first.
 */
function Reviewed({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const { data, isLoading, error } = useAsync(
    (signal) => provider.paymentsByStatus('all', signal),
    [provider],
  );

  if (isLoading || error) return null;

  const decided = (data ?? []).filter((payment) => payment.status !== 'Declared');

  if (decided.length === 0) return null;

  return (
    <Card>
      <div className="cma-stack cma-stack--tight">
        <div className="provider-strong">Already reviewed</div>

        {decided.slice(0, 20).map((payment) => (
          <div key={payment.id} className="provider-linerow">
            <span>
              {payment.tenantName}
              <span className="provider-muted"> · {payment.invoiceNumber}</span>
            </span>

            <span className="provider-chips">
              <span className="tabular">
                {formatMoney(payment.amount, payment.currency)}
              </span>
              <Badge tone={payment.status === 'Confirmed' ? 'success' : 'danger'}>
                {payment.status}
              </Badge>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PaymentCard({
  api,
  payment,
  onReviewed,
}: {
  api: ApiClient;
  payment: ProviderPaymentRow;
  onReviewed: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const review = async (confirm: boolean) => {
    setBusy(true);
    setError(null);

    try {
      await provider.reviewPayment(payment.id, confirm, note.trim() || null);

      toast.success(
        confirm ? `Confirmed against ${payment.invoiceNumber}` : 'Rejected, and the customer told why',
      );

      onReviewed();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="cma-stack cma-stack--tight">
        {error && <Alert tone="danger">{error}</Alert>}

        <div className="provider-actionrow">
          <div>
            <div className="provider-strong">{payment.tenantName}</div>
            <div className="provider-muted">
              {payment.invoiceNumber} · {payment.method} · paid {payment.paidOn}
            </div>
          </div>

          <div className="provider-amount">{formatMoney(payment.amount, payment.currency)}</div>
        </div>

        <div className="cma-row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <Badge tone="neutral">Reference: {payment.reference || 'none given'}</Badge>
          <Badge tone="neutral">Declared {formatDateTime(payment.declaredAtUtc)}</Badge>
        </div>

        {payment.customerNote && (
          <p className="provider-note">“{payment.customerNote}”</p>
        )}

        {rejecting ? (
          <div className="cma-stack cma-stack--tight">
            <Input
              value={note}
              autoFocus
              placeholder="Why is it being rejected? The customer will read this."
              onChange={(event) => setNote(event.target.value)}
            />

            <div className="cma-row" style={{ gap: 8 }}>
              <Button
                variant="danger"
                isLoading={busy}
                disabled={!note.trim()}
                onClick={() => void review(false)}
              >
                Reject
              </Button>
              <Button variant="ghost" onClick={() => setRejecting(false)}>
                Cancel
              </Button>
            </div>

            <p className="provider-muted">
              A reason is required. The customer believes they have paid, and “rejected” on its
              own starts an argument instead of resolving one.
            </p>
          </div>
        ) : (
          <div className="cma-row" style={{ gap: 8 }}>
            <Button variant="primary" isLoading={busy} onClick={() => void review(true)}>
              Confirm received
            </Button>
            <Button variant="ghost" onClick={() => setRejecting(true)}>
              Reject
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
